/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppStore, getAppSelection } from '@alfresco/aca-shared/store';
import { take } from 'rxjs/operators';

/**
 * Toolbar / context-menu action that starts a workflow for the selected node,
 * by navigating to the Start Workflow page with the node pre-attached.
 */
@Component({
  selector: 'app-start-workflow-action',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      *ngIf="data?.iconButton"
      mat-icon-button
      [attr.aria-label]="data?.title || 'Start workflow'"
      [matTooltip]="data?.title || 'Start workflow'"
      (click)="onClick()"
    >
      <mat-icon>rocket_launch</mat-icon>
    </button>

    <button *ngIf="data?.menuButton" mat-menu-item (click)="onClick()">
      <mat-icon>rocket_launch</mat-icon>
      <span>{{ data?.title || 'Start workflow' }}</span>
    </button>
  `
})
export class StartWorkflowActionComponent {
  private readonly store = inject<Store<AppStore>>(Store);
  private readonly router = inject(Router);

  @Input() data: { title?: string; menuButton?: boolean; iconButton?: boolean };

  onClick(): void {
    this.store
      .select(getAppSelection)
      .pipe(take(1))
      .subscribe((selection) => {
        const node = selection?.first || selection?.file || selection?.folder || selection?.nodes?.[0];
        const id = node?.entry?.id;
        this.router.navigate(['/workflows/start'], { queryParams: id ? { nodeId: id } : {} });
      });
  }
}
